<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useRouter } from 'vue-router';
import {
  Table,
  TableIconText,
  UserAvatar,
  ButtonType,
  CustomButton,
  TableColumnType
} from '@/components';
import { formattedDate, DATE_FORMAT_2, routeConstants } from '@/utils';
import { CaseModel } from '@/models';
import { useCaseStore } from '@/stores/case';
import { useEntryListStore } from '@/stores/entryList';

const props = defineProps({
  selectedCases: {
    type: Object as PropType<CaseModel[]>,
    required: true
  }
});

const caseStore = useCaseStore();
const entryStore = useEntryListStore();
if (!entryStore.selectedCase) {
  caseStore.getCases();
}

const emit = defineEmits(['update:selectedCases', 'onNewCase', 'onCaseClick']);

const selectedRows = computed({
  get: () => props.selectedCases,
  set: value => {
    emit('update:selectedCases', value);
  }
});

const onNewCase = () => {
  emit('onNewCase');
};

const router = useRouter();
const onCaseClick = (caseModel: CaseModel) => {
  emit('onCaseClick');
  entryStore.resetEntryList(caseModel);
  router.push({
    name: routeConstants.CASE.name,
    params: {
      identifier: caseModel.identifier
    }
  });
};
</script>

<template>
  <div class="cases-list-root">
    <Table :columns="caseStore.tableColumns"
      :data-list="caseStore.caseList"
      :loading-state="caseStore.casesLoader.isLoading"
      :selection="true"
      :totalRecords="caseStore.totalCasesCount"
      v-model:selectedRows="selectedRows"
      :rows="caseStore.pageLimit"
      :currentRow="(caseStore.currentPage - 1) * caseStore.pageLimit"
      @onPageChange="caseStore.onPageChange"
      @onRowClick="onCaseClick"
      @onSortFieldChange="caseStore.setSort"
      @onSortOrderChange="caseStore.setSortOrder"
      presort="createdAt">
      <template #bodyContent="{ data, col }">
        <div class="case-item" v-if="col.type === TableColumnType.TEXT_WTIH_ICON">
          <TableIconText :text="data[col.key]"
            :icon="col.icon" />
          <FontAwesomeIcon :icon="[`fas`, `lock`]" v-if="data['lockedAt']" class="lock"/>
        </div>

        <div v-if="col.type === TableColumnType.TEXT">
          {{
            col.key === "createdAt"
                ? formattedDate(data[col.key], DATE_FORMAT_2)
                : data[col.key]
            }}
        </div>

        <UserAvatar :avatarUrl="data.creatorAvatar"
          :userName="data[col.key] ?? 'System Generated'"
          :size="28"
          v-if="col.type === TableColumnType.USER"/>
      </template>
      <template #emptyContent>
       <div class="empty-content-container">
        <div class="empty-content">
          <FontAwesomeIcon :icon="[`fal`, 'folder']" class="file-icon"/>
          <h3 class="no-entries-label">No cases created yet</h3>
          <div class="new-entry">
            <div class="new-entry-text">To create a case click on</div>
            <CustomButton text="New Case"
              :type="ButtonType.POSITIVE"
              :margin-left="16"
              @onClick="onNewCase"
              :icon="{ icon: 'folder', style: 'far' }"/>
          </div>
        </div>
       </div>
      </template>
    </Table>
  </div>
</template>

<style scoped lang="scss">
.cases-list-root {
  height: 100%;
}
.case-item {
  display: flex;
  align-items: center;

  .lock {
    margin-left: 8px;
    font-size: 16px;
  }
}
.empty-content-container {
  padding: 50px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  .empty-content {
    text-align: center;
    .file-icon {
      font-size: 100px;
      margin: 16px 0px;
    }

    .no-entries-label {
      font-weight: bold;
    }

    .new-entry {
      margin: 16px 0px;
      display: flex;
      align-items: center;
    }
  }
}
</style>
